import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Select, Store } from '@ngxs/store';
import { AnnualDeclarationFormCollection } from 'app/company/form/annual-declaration-form/shared/model/annual-declaration.model';
import { AnnualDeclarationState } from 'app/company/form/annual-declaration-form/shared/state/annual-declaration.state';
import { ToastrService } from 'ngx-toastr';
import { EMPTY, Observable, Subject } from 'rxjs';
import { catchError, map, takeUntil, tap } from 'rxjs/operators';
import { CompleteAnnualDeclarationForm, SetProcessingCompanySubscription } from '~app/company/form/annual-declaration-form/shared/state/annual-declaration.actions';
import { UserMemberService } from '~app/services/user-member.service';
import { CurrencyCustomPipe } from '~app/shared/pipes/currency.pipe';
import { environment } from '~env';
import { AnnualDeclarationFormService } from '../../company/form/annual-declaration-form/shared/service/annual-declaration-form.service';
import { CartItem } from '../cart/state/cart.model';
import { CartState } from '../cart/state/cart.state';
import { PaymentErrorDialog } from '../dialog/payment-error/payment-error-dialog';
import { ReceiptActions } from '../receipt/state';
import { ReceiptModel } from '../receipt/state/receipt.model';
import { CheckoutDataService } from './state/checkout-data.service';
import { CheckoutInfo } from './state/checkout.model';

@Component({
    selector: 'odq-checkout',
    templateUrl: './checkout.component.html',
    styleUrls: ['./checkout.component.scss'],
    providers: [CurrencyCustomPipe],
    standalone: false
})
export class CheckoutComponent implements OnInit, OnDestroy {

  storeId = environment.shoppingCart.companyMonerisStoreId;

  @Select(AnnualDeclarationState.formId) formId$: Observable<string>;
  @Select(AnnualDeclarationState.year) year$: Observable<number>;
  @Select(AnnualDeclarationState.legalEntityNumber) legalEntityNumber$: Observable<string>;
  @Select(AnnualDeclarationState.formCollection) formCollection$: Observable<AnnualDeclarationFormCollection>;

  cartItems$ = this.store.select<CartItem[]>(CartState.items);
  cartTotal$ = this.store.select<number>(CartState.total);

  paymentError = false;
  paymentErrorMessage = '';
  totalAmount: number;
  cartItems: any[];
  isCreditCardProcessing = false;
  isPaymentAvailable = false;
  memberId: string;
  year: number;

  private dataKey: string;
  private formId: string;
  private legalEntityNumber: string;

  destroy$ = new Subject<void>();

  constructor(
    public dialog: MatDialog,
    private translate: TranslateService,
    private checkoutDataService: CheckoutDataService,
    private router: Router,
    private route: ActivatedRoute,
    private annualDeclarationFormService: AnnualDeclarationFormService,
    private store: Store,
    private cdr: ChangeDetectorRef,
    private currencyPipe: CurrencyCustomPipe,
    private userService: UserMemberService,
    private toastrService: ToastrService,
  ) { }

  ngOnInit() {

    this.userService.memberId$
      .pipe(takeUntil(this.destroy$))
      .subscribe(memberId => {
        this.memberId = memberId;
      });

    this.isPaymentAvailable = true;
    this.cartItems = [];

      this.cartTotal$
      .pipe(takeUntil(this.destroy$))
      .subscribe(total => {
        this.totalAmount = total;
        this.refresh();
      });

    this.cartItems$
      .pipe(takeUntil(this.destroy$))
      .subscribe(items => {
        this.cartItems = items;
      });

    this.formId$.pipe(
      takeUntil(this.destroy$),
    ).subscribe(id => {
      this.formId = id;
    });

    this.legalEntityNumber$.pipe(
      takeUntil(this.destroy$),
    ).subscribe(len => {
      this.legalEntityNumber = len;
    });

    this.year$.pipe(
      takeUntil(this.destroy$),
    ).subscribe(data => {
      this.year = data;
    });

  }

  refresh() {
    try {
      this.cdr.detectChanges();
    }catch {
      console.error('Error in change detection');
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  doPayment($event) {
    this.isCreditCardProcessing = true;
    this.dataKey = $event.dataKey;

    const cartInfo: Array<any> = [];
    this.cartItems.forEach(element => {
      cartInfo.push({
        quantity: element.quantity,
        product: {
          id: element.productId,
          title: element.title,
          description: element.description,
          price: element.price,
        },
      });
    });

    const checkoutData: CheckoutInfo = {
      id: '',
      societeId: this.legalEntityNumber,
      amount: this.totalAmount,
      data_key: this.dataKey,
      feeType: 'AN',
      bin: $event.bin,
      formid: this.formId,
      nbrEstablishment: this.annualDeclarationFormService.getNumberOfEstablishments(),
      permitNumber: this.annualDeclarationFormService.getRespondentPermitNumber(),
      items: cartInfo,
      memberId: this.memberId,
      addressPayer: this.annualDeclarationFormService.getAddress(),
      year: this.year
    };

    this.setSubscriptionProcessing(true);
    this.checkoutDataService.sendCreditCardPayment(checkoutData).pipe(

      tap(data => {
        const isError = this.isInvalidPaymentResponse(data);
        if (isError) {
          this.setSubscriptionProcessing(false);
          this.isCreditCardProcessing = false;
          // TODO change gestionodqfarp Moneris error handling in order to provide the returned code by Moneris
          if (data?.result === 'Transaction refused' || data?.result === 'Refused') {
            this.openErrorDialog('51', data?.result);
          }
          else {
            this.openErrorDialog('999', data?.message || data?.result || this.getPaymentUnprocessedMessage());
          }

        } else {
          const receipt = Object.assign(new ReceiptModel(), data);
          receipt.metadata = {
            formType: 'AN',
            societeId: checkoutData.societeId,
            year: new Date().getFullYear(),
          };

          this.store.dispatch(new ReceiptActions.AddReceipt(receipt));

          this.isCreditCardProcessing = false;
          this.setSubscriptionProcessing(false);
          this.router.navigate(['receipt'], { relativeTo: this.route.parent });
        }
      }),
      map(data => {
        if (data.result === 'Accepted') {
          this.store.dispatch(new CompleteAnnualDeclarationForm(data));
        }
        else {
          this.isPaymentAvailable = false;
        }

      }),
      takeUntil(this.destroy$),
      catchError(err => {
        this.setSubscriptionProcessing(false);
        this.isCreditCardProcessing = false;
     
        if (this.isManagedLegalEntityPaymentError(err))
          return EMPTY;
      
        this.paymentError = true;
        this.paymentErrorMessage = this.getPaymentUnprocessedMessage();
        throw err;
      }),
    ).subscribe(); 
  }

  setSubscriptionProcessing(value: boolean) {
    this.store.dispatch(new SetProcessingCompanySubscription(value));
  }

  openErrorDialog(errorCode: string, errorMessage: string) {
    const dialogRef = this.dialog.open(PaymentErrorDialog, {
      minWidth: '200px',
      data: { code: errorCode, message: errorMessage },
    });

    dialogRef.afterClosed().subscribe(result => {
      this.paymentError = true;
      this.paymentErrorMessage = this.getPaymentUnprocessedMessage();
      this.cdr.detectChanges();
    });
  }

  retryPayment() {
    this.isPaymentAvailable = true;
  }

  getAmount(amount: number) {
    return this.currencyPipe.transform(amount);
  }

  private getPaymentUnprocessedMessage() {
    let result = '';
    this.translate.get('shoppingCart.payment.error.unProcessedPayment').subscribe(res => {
      result = res;
    });

    return result;
  }

   /**
   * We check the error content and the location to know if we can manage certain error(s) in the Legal Entity (société) section
   * if so then we reload the cart otherwise an error message will be shown
   * @param exception 
   * @returns true if the error is managed. False then we let the error management take care of the error
   */
  isManagedLegalEntityPaymentError(exception: any)
  {
    try {
      const errorDesc = new String(exception?.error)
      const routeParts = this.router.url.split('/');
      
      if (routeParts?.length > 1 && routeParts[1] === 'company' && errorDesc.indexOf('is different than bill amount') > -1)
      {
        const societeId = this.route.snapshot.paramMap.get('societeId');
        this.annualDeclarationFormService.preparePayment(societeId);
        this.toastrService.error(
          this.translate.instant('shoppingCart.payment.error.priceChange'),
          this.translate.instant('shoppingCart.payment.error.unProcessedPayment'),
          {
            closeButton: true,
            timeOut: 10000,
            onActivateTick: true,
          },
        );
        return true
      }

      return false;
    }
    catch {
      return false;
    }
  }

  private isInvalidPaymentResponse(data: any): boolean {
    if (!data) {
      return true;
    }

    const result = this.normalizeString(data.result);
    const responseCodeText = this.normalizeString(data.responseCode);
    const responseCode = Number.parseInt(responseCodeText, 10);

    const hasInvalidResponseCode = !responseCodeText || Number.isNaN(responseCode) || responseCode > 49;
    const hasMissingCriticalFields = this.isNullLike(data.sequence)
      || this.isNullLike(data.numConfirmation)
      || this.isNullLike(data.orderId)
      || this.isNullLike(data.amount);

    return result !== 'Accepted' || hasInvalidResponseCode || hasMissingCriticalFields;
  }

  private normalizeString(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }

    return String(value).trim();
  }

  private isNullLike(value: any): boolean {
    const text = this.normalizeString(value);
    return !text || text.toLowerCase() === 'null';
  }
}
